import {
  CITY_CENTER,
  CITY_CLEARING,
  CITY_RADIUS,
  CITY_WALL_RADIUS,
  GUARD_NAMES,
  MONUMENT_NAME,
  ARMOURER_NAME,
  ARMOURER_OFFSET,
  MONUMENT_OFFSET,
  POI_CHANCE_PER_CHUNK,
  POI_CHUNK_SIZE,
  POI_CITY_MARGIN,
  POI_CLEARING,
  QUEST_GIVER_NAME,
  QUEST_GIVER_OFFSET,
  ROCK_COUNT,
  SAFE_ZONE_RADIUS,
  SPAWN_POINT,
  TRADER_NAME,
  TRADER_OFFSET,
  TREE_COUNT,
  WEAPONER_NAME,
  WEAPONER_OFFSET,
  WORLD_SEED,
  WORLD_SIZE,
} from './constants';
import { blockedAt } from './collision';
import { TAU, dist2 } from './math';
import { Rng } from './rng';
import type { NpcInfo, Obstacle, PoiKind, PointOfInterest, WorldInfo } from './types';

/** The four compass gates, in radians. */
const GATES = [0, Math.PI / 2, Math.PI, -Math.PI / 2];
/** Half-width of each gateway, in radians. */
const GATE_HALF_WIDTH = 0.08;

/** A uniform grid so scenery can be placed without an O(n^2) overlap scan. */
class PlacementGrid {
  private cell = 320;
  private buckets = new Map<number, Obstacle[]>();

  private key(cx: number, cy: number): number {
    return cx * 100003 + cy;
  }

  add(o: Obstacle): void {
    const k = this.key(Math.floor(o.x / this.cell), Math.floor(o.y / this.cell));
    const list = this.buckets.get(k);
    if (list) list.push(o);
    else this.buckets.set(k, [o]);
  }

  /** True when a circle at (x, y) is clear of everything placed so far. */
  clear(x: number, y: number, radius: number, gap: number): boolean {
    const reach = radius + gap;
    const x0 = Math.floor((x - reach) / this.cell);
    const x1 = Math.floor((x + reach) / this.cell);
    const y0 = Math.floor((y - reach) / this.cell);
    const y1 = Math.floor((y + reach) / this.cell);
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        const list = this.buckets.get(this.key(cx, cy));
        if (!list) continue;
        for (const o of list) {
          const min = radius + o.r + gap;
          if (dist2(x, y, o.x, o.y) < min * min) return false;
        }
      }
    }
    return true;
  }
}

export interface GeneratedWorld extends WorldInfo {
  obstacles: Obstacle[];
  /** Landmarks stamped by the chunk generator, kept for gameplay systems. */
  pointsOfInterest: PointOfInterest[];
}

/** Strips the bulky scenery, leaving just what the network needs to describe. */
export function worldInfo(world: GeneratedWorld): WorldInfo {
  return {
    width: world.width,
    height: world.height,
    spawn: world.spawn,
    safeZone: world.safeZone,
    cityRadius: CITY_RADIUS,
    npcs: world.npcs,
  };
}

/* ------------------------------------------------------------ points of interest */

type Place = (kind: Obstacle['kind'], x: number, y: number, r: number) => void;

interface PoiDef {
  kind: PoiKind;
  name: string;
  /** Footprint radius: pieces stay inside this and it reserves open ground. */
  radius: number;
  build: (x: number, y: number, rng: Rng, place: Place) => void;
}

/**
 * A deterministic value derived only from the world seed and a chunk's
 * coordinates. This is what lets the client and the server agree on where the
 * landmarks are without ever sending them over the wire.
 */
function chunkSeed(seed: number, cx: number, cy: number): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (cx + 0x9e3779b9), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (cy + 0x165667b1), 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

/** Spreads `count` pieces evenly around a ring, each nudged off the circle. */
function ringPlace(
  x: number,
  y: number,
  count: number,
  distance: number,
  jitter: number,
  rng: Rng,
  place: Place,
  kind: Obstacle['kind'],
  r: number,
): void {
  const phase = rng.range(0, TAU);
  for (let i = 0; i < count; i++) {
    const a = phase + (i / count) * TAU + rng.range(-0.18, 0.18);
    const d = distance + rng.range(-jitter, jitter);
    place(kind, x + Math.cos(a) * d, y + Math.sin(a) * d, r + rng.range(-2, 2));
  }
}

/** Scatter a couple of loose props without landing them on the landmark core. */
function props(
  x: number,
  y: number,
  items: Array<[Obstacle['kind'], number]>,
  rng: Rng,
  place: Place,
): void {
  for (const [kind, r] of items) {
    const a = rng.range(0, TAU);
    const d = rng.range(120, 200);
    place(kind, x + Math.cos(a) * d, y + Math.sin(a) * d, r);
  }
}

const POIS: PoiDef[] = [
  {
    kind: 'camp',
    name: 'bandit camp',
    radius: 360,
    build: (x, y, rng, place) => {
      place('campfire', x, y, 27);
      ringPlace(x, y, 3, 205, 28, rng, place, 'tent', 47);
      ringPlace(x, y, 6, 315, 30, rng, place, 'fence', 15);
      props(x, y, [['crate', 34], ['barrel', 30], ['crate', 32]], rng, place);
    },
  },
  {
    kind: 'hamlet',
    name: 'hamlet',
    radius: 470,
    build: (x, y, rng, place) => {
      place('well', x, y, 40);
      ringPlace(x, y, 5, 300, 22, rng, place, 'hut', 76);
      ringPlace(x, y, 8, 425, 24, rng, place, 'fence', 15);
      props(x, y, [['crate', 32], ['barrel', 28]], rng, place);
    },
  },
  {
    kind: 'graveyard',
    name: 'graveyard',
    radius: 460,
    build: (x, y, rng, place) => {
      place('crypt', x, y - 130, 68);
      place('monolith', x, y + 60, 26);
      ringPlace(x, y, 10, 235, 26, rng, place, 'gravestone', 20);
      ringPlace(x, y, 6, 360, 30, rng, place, 'gravestone', 18);
      props(x, y, [['rock', 22], ['rock', 20]], rng, place);
    },
  },
  {
    kind: 'chapel',
    name: 'chapel',
    radius: 430,
    build: (x, y, rng, place) => {
      place('chapel', x, y, 92);
      place('monolith', x, y + 175, 24);
      ringPlace(x, y, 6, 265, 30, rng, place, 'gravestone', 20);
      props(x, y, [['tree', 34]], rng, place);
    },
  },
  {
    kind: 'ruins',
    name: 'ruins',
    radius: 420,
    build: (x, y, rng, place) => {
      place('monolith', x, y, 30);
      ringPlace(x, y, 7, 265, 34, rng, place, 'pillar', 25);
      ringPlace(x, y, 5, 175, 26, rng, place, 'rock', 21);
      props(x, y, [['rock', 24], ['rock', 19], ['pillar', 22]], rng, place);
    },
  },
  {
    kind: 'shrine',
    name: 'shrine',
    radius: 320,
    build: (x, y, rng, place) => {
      place('monolith', x, y, 44);
      ringPlace(x, y, 6, 180, 20, rng, place, 'monolith', 24);
      ringPlace(x, y, 4, 270, 24, rng, place, 'rock', 20);
    },
  },
  {
    kind: 'farmstead',
    name: 'farmstead',
    radius: 470,
    build: (x, y, rng, place) => {
      place('hut', x - 195, y - 30, 84);
      place('hut', x + 190, y + 40, 70);
      place('well', x, y + 160, 34);
      place('scarecrow', x, y - 175, 24);
      ringPlace(x, y, 9, 405, 26, rng, place, 'fence', 15);
      props(x, y, [['crate', 30], ['barrel', 27]], rng, place);
    },
  },
  {
    kind: 'watchtower',
    name: 'watchtower',
    radius: 360,
    build: (x, y, rng, place) => {
      place('tower', x, y, 86);
      place('campfire', x + 135, y + 95, 25);
      place('tent', x - 155, y + 80, 44);
      ringPlace(x, y, 4, 235, 24, rng, place, 'rock', 22);
    },
  },
];

/**
 * Builds the static world from a seed.
 *
 * This runs on BOTH the server and the client. The world is far too large to
 * ship over the wire, and because the generator is fully deterministic (its own
 * seeded PRNG, no Math.random) both sides end up with byte-identical scenery -
 * which is what lets client-side movement prediction collide against exactly
 * the same rocks the server does.
 */
export function generateWorld(seed: number = WORLD_SEED): GeneratedWorld {
  const rng = new Rng(seed);
  const obstacles: Obstacle[] = [];
  const grid = new PlacementGrid();

  const push = (kind: Obstacle['kind'], x: number, y: number, r: number): void => {
    const o: Obstacle = { x, y, r, kind };
    obstacles.push(o);
    grid.add(o);
  };

  /* ------------------------------------------------------------------ city wall */

  const blockRadius = 62;
  const steps = Math.round((TAU * CITY_WALL_RADIUS) / (blockRadius * 1.42));
  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * TAU;
    let onGate = false;
    for (const gate of GATES) {
      let delta = angle - gate;
      while (delta > Math.PI) delta -= TAU;
      while (delta < -Math.PI) delta += TAU;
      if (Math.abs(delta) < GATE_HALF_WIDTH) onGate = true;
    }
    if (onGate) continue;

    const radius = CITY_WALL_RADIUS + rng.range(-7, 7);
    push(
      'wall',
      CITY_CENTER.x + Math.cos(angle) * radius,
      CITY_CENTER.y + Math.sin(angle) * radius,
      blockRadius + rng.range(-5, 5),
    );
  }

  // Corner towers, set just inside the wall between the gates.
  for (const angle of [Math.PI / 4, (Math.PI * 3) / 4, (-Math.PI * 3) / 4, -Math.PI / 4]) {
    push(
      'tower',
      CITY_CENTER.x + Math.cos(angle) * (CITY_WALL_RADIUS - 150),
      CITY_CENTER.y + Math.sin(angle) * (CITY_WALL_RADIUS - 150),
      104,
    );
  }

  /* -------------------------------------------------------------------- the plaza */

  push('fountain', CITY_CENTER.x, CITY_CENTER.y, 124);

  const ring = (angles: number[], distance: number, radius: number): void => {
    for (const degrees of angles) {
      const angle = (degrees * Math.PI) / 180;
      push(
        'hut',
        CITY_CENTER.x + Math.cos(angle) * distance,
        CITY_CENTER.y + Math.sin(angle) * distance,
        radius,
      );
    }
  };

  ring([20, 45, 70, 110, 135, 160, 200, 225, 250, 290, 315, 340], 880, 96);
  ring([30, 120, 210, 300], 1210, 88);

  /* ---------------------------------------------------------------------- clutter */

  const traderX = CITY_CENTER.x + TRADER_OFFSET.x;
  const traderY = CITY_CENTER.y + TRADER_OFFSET.y;
  const clutter: Array<[Obstacle['kind'], number, number, number]> = [
    ['crate', traderX + 150, traderY - 80, 42],
    ['barrel', traderX + 220, traderY + 70, 38],
    ['barrel', traderX - 60, traderY - 200, 38],
    ['crate', traderX - 210, traderY - 150, 40],
    ['crate', CITY_CENTER.x + 150, CITY_CENTER.y - 720, 44],
    ['barrel', CITY_CENTER.x - 760, CITY_CENTER.y - 200, 40],
    ['crate', CITY_CENTER.x + 980, CITY_CENTER.y + 240, 42],
    ['barrel', CITY_CENTER.x - 420, CITY_CENTER.y + 900, 40],
    ['barrel', CITY_CENTER.x + 640, CITY_CENTER.y + 880, 40],
  ];
  for (const [kind, x, y, r] of clutter) push(kind, x, y, r);

  /* ------------------------------------------------------------------------ npcs */

  const guardRing = CITY_WALL_RADIUS - 130;
  const npcs: NpcInfo[] = [
    { id: 1, name: TRADER_NAME, x: traderX, y: traderY, role: 'trader' },
    {
      id: 6,
      name: WEAPONER_NAME,
      x: CITY_CENTER.x + WEAPONER_OFFSET.x,
      y: CITY_CENTER.y + WEAPONER_OFFSET.y,
      role: 'weaponeer',
    },
    {
      id: 7,
      name: ARMOURER_NAME,
      x: CITY_CENTER.x + ARMOURER_OFFSET.x,
      y: CITY_CENTER.y + ARMOURER_OFFSET.y,
      role: 'armourer',
    },
    {
      id: 2,
      name: GUARD_NAMES[0],
      x: CITY_CENTER.x + Math.cos(Math.PI / 2 - 0.05) * guardRing,
      y: CITY_CENTER.y + Math.sin(Math.PI / 2 - 0.05) * guardRing,
      role: 'guard',
    },
    {
      id: 3,
      name: GUARD_NAMES[1],
      x: CITY_CENTER.x + Math.cos(Math.PI / 2 + 0.05) * guardRing,
      y: CITY_CENTER.y + Math.sin(Math.PI / 2 + 0.05) * guardRing,
      role: 'guard',
    },
    {
      id: 4,
      name: QUEST_GIVER_NAME,
      x: CITY_CENTER.x + QUEST_GIVER_OFFSET.x,
      y: CITY_CENTER.y + QUEST_GIVER_OFFSET.y,
      role: 'questgiver',
    },
    {
      id: 5,
      name: MONUMENT_NAME,
      x: CITY_CENTER.x + MONUMENT_OFFSET.x,
      y: CITY_CENTER.y + MONUMENT_OFFSET.y,
      role: 'monument',
    },
  ];

  /* ------------------------------------------------- points of interest (chunks) */

  // Walk the chunk grid in a fixed order so the result never depends on the
  // order chunks are visited. Each chunk draws its own RNG from the seed, so
  // most produce nothing and the rare one stamps a landmark in place.
  const pointsOfInterest: PointOfInterest[] = [];
  const placePoi: Place = (kind, x, y, r) => push(kind, Math.round(x), Math.round(y), Math.round(r));
  const chunks = Math.ceil(WORLD_SIZE / POI_CHUNK_SIZE);

  for (let cy = 0; cy < chunks; cy++) {
    for (let cx = 0; cx < chunks; cx++) {
      const chunkRng = new Rng(chunkSeed(seed, cx, cy));
      if (!chunkRng.chance(POI_CHANCE_PER_CHUNK)) continue;

      const def = chunkRng.pick(POIS);
      const inset = def.radius + 40;
      const span = POI_CHUNK_SIZE - inset * 2;
      if (span <= 0) continue;

      const px = cx * POI_CHUNK_SIZE + inset + chunkRng.range(0, span);
      const py = cy * POI_CHUNK_SIZE + inset + chunkRng.range(0, span);

      // Never crowd the sanctuary, and never overlap an earlier landmark.
      const keepOut = SAFE_ZONE_RADIUS + def.radius + POI_CITY_MARGIN;
      if (dist2(px, py, CITY_CENTER.x, CITY_CENTER.y) < keepOut * keepOut) continue;
      if (!grid.clear(px, py, def.radius, POI_CLEARING)) continue;

      def.build(px, py, chunkRng, placePoi);
      pointsOfInterest.push({
        kind: def.kind,
        name: def.name,
        x: Math.round(px),
        y: Math.round(py),
        radius: def.radius,
      });
    }
  }

  /* ------------------------------------------------------------------ wilderness */

  const margin = 90;
  const keepOut = CITY_RADIUS + CITY_CLEARING;
  const tryWild = (kind: 'rock' | 'tree', minR: number, maxR: number): void => {
    for (let attempt = 0; attempt < 24; attempt++) {
      const r = rng.range(minR, maxR);
      const x = rng.range(margin + r, WORLD_SIZE - margin - r);
      const y = rng.range(margin + r, WORLD_SIZE - margin - r);
      if (dist2(x, y, CITY_CENTER.x, CITY_CENTER.y) < (keepOut + r) ** 2) continue;
      // Leave each landmark the open clearing it was promised.
      if (pointsOfInterest.some((p) => dist2(x, y, p.x, p.y) < (p.radius + r + POI_CLEARING) ** 2)) {
        continue;
      }
      if (!grid.clear(x, y, r, 34)) continue;
      push(kind, Math.round(x), Math.round(y), Math.round(r));
      return;
    }
  };

  for (let i = 0; i < ROCK_COUNT; i++) tryWild('rock', 24, 58);
  for (let i = 0; i < TREE_COUNT; i++) tryWild('tree', 26, 52);

  return {
    width: WORLD_SIZE,
    height: WORLD_SIZE,
    spawn: { ...SPAWN_POINT },
    obstacles,
    pointsOfInterest,
    safeZone: { x: CITY_CENTER.x, y: CITY_CENTER.y, r: SAFE_ZONE_RADIUS },
    cityRadius: CITY_RADIUS,
    npcs,
  };
}

/** Spatial bucket index so collision checks stay cheap with thousands of obstacles. */
export class ObstacleGrid {
  private cell = 260;
  private cols: number;
  private rows: number;
  private buckets: Obstacle[][];

  constructor(obstacles: Obstacle[], worldSize: number) {
    this.cols = Math.ceil(worldSize / this.cell) + 1;
    this.rows = this.cols;
    this.buckets = Array.from({ length: this.cols * this.rows }, () => []);
    for (const o of obstacles) {
      const cx = Math.floor(o.x / this.cell);
      const cy = Math.floor(o.y / this.cell);
      this.buckets[cy * this.cols + cx].push(o);
    }
  }

  /** Obstacles within `radius` of a point. */
  near(x: number, y: number, radius: number): Obstacle[] {
    const out: Obstacle[] = [];
    const x0 = Math.max(0, Math.floor((x - radius) / this.cell));
    const x1 = Math.min(this.cols - 1, Math.floor((x + radius) / this.cell));
    const y0 = Math.max(0, Math.floor((y - radius) / this.cell));
    const y1 = Math.min(this.rows - 1, Math.floor((y + radius) / this.cell));
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        for (const o of this.buckets[cy * this.cols + cx]) out.push(o);
      }
    }
    return out;
  }

  /** True when a circle would overlap any obstacle. */
  blocked(x: number, y: number, radius: number): boolean {
    return blockedAt(x, y, radius, this.near(x, y, radius + 300));
  }
}
